import startCase from 'lodash/startCase';

import { Block } from '@eluve/llm-outputs';

import type { Spec } from './catalog';
import type { ExpandableAnalytics, LinkAnalytics } from './sdui-types';
import { SpecBuilder } from './spec-builder';

export interface StructuredPrevisitInsightsInput {
  patientSummary: string;
  previsitInsights: Array<{
    title: string;
    description: string;
    citationIds: string[];
    citedTextSnippets?: Record<string, string>;
  }>;
  citations: Array<{
    id: string;
    type: string;
    date: string | null;
    title?: string;
    data?: unknown;
  }>;
}

export type Citation = StructuredPrevisitInsightsInput['citations'][0];

const SOAP_KEYS = ['subjective', 'objective', 'assessment', 'plan'] as const;

const isSoapNote = (data: Record<string, unknown>): boolean => {
  return SOAP_KEYS.every((key) => key in data);
};

const processEntry = (key: string, value: unknown, blocks: Block[]): void => {
  const label = startCase(key);

  if (typeof value === 'string' && value.trim()) {
    blocks.push({
      type: 'text',
      key,
      label,
      text: value,
    });
  } else if (value && typeof value === 'object' && !Array.isArray(value)) {
    const nestedBlocks = convertDataToBlocks(value as Record<string, unknown>);
    if (nestedBlocks.length > 0) {
      blocks.push({
        type: 'group',
        key,
        label,
        blocks: nestedBlocks,
      });
    }
  }
};

const convertDataToBlocks = (data: Record<string, unknown>): Block[] => {
  const blocks: Block[] = [];

  if (isSoapNote(data)) {
    // Process SOAP fields in the correct order
    for (const key of SOAP_KEYS) {
      processEntry(key, data[key], blocks);
    }

    // Process any remaining non-SOAP fields
    for (const [key, value] of Object.entries(data)) {
      if (!SOAP_KEYS.includes(key as (typeof SOAP_KEYS)[number])) {
        processEntry(key, value, blocks);
      }
    }
  } else {
    for (const [key, value] of Object.entries(data)) {
      processEntry(key, value, blocks);
    }
  }

  return blocks;
};

const convertChartToBlocks = (citation: Citation): Block[] => {
  const data = citation.data as Record<string, unknown> | undefined;

  if (!data || typeof data !== 'object') return [];

  return convertDataToBlocks(data);
};

const buildExpandableAnalytics = (
  insightTitle: string,
  insightDescription: string,
): ExpandableAnalytics => ({
  onExpand: {
    event: 'insights:section_expanded',
    properties: { insightTitle, insightDescription },
  },
  onCollapse: {
    event: 'insights:section_collapsed',
    properties: { insightTitle, insightDescription },
  },
});

const buildCitationLinkAnalytics = (
  citation: Citation,
  number: number,
  location: 'inline' | 'sources_section',
): LinkAnalytics => ({
  onClick: {
    event: 'insights:citation_clicked',
    properties: {
      location,
      citation_id: citation.id,
      citation_type: citation.type,
      citation_number: number,
    },
  },
});

export interface TransformInsightsOptions {
  /**
   * Zero-based indices of insights that should be rendered as expanded by
   * default. Any insight whose index is not included is rendered collapsed.
   * When omitted, all insights are collapsed.
   */
  expandedInsightIndices?: readonly number[];
}

/**
 * Transforms raw structured previsit insights into a json-render Spec.
 */
export const transformInsightsToSpec = (
  raw: StructuredPrevisitInsightsInput,
  options: TransformInsightsOptions = {},
): Spec => {
  const expandedIndices = new Set(options.expandedInsightIndices ?? []);
  const sb = new SpecBuilder();
  const rootChildren: string[] = [];

  const citationMap = new Map<
    string,
    {
      citation: Citation;
      number: number;
    }
  >();

  const usedCitationIds = new Set<string>();
  for (const insight of raw.previsitInsights) {
    for (const citationId of insight.citationIds) {
      usedCitationIds.add(citationId);
    }
  }

  const parseDate = (dateStr: string | null): number => {
    if (!dateStr) return Infinity;
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? Infinity : parsed.getTime();
  };

  // Collect used citations and sort by date descending so that
  // citation numbers match the display order in the SOURCES section
  const usedCitations = [...usedCitationIds]
    .map((id) => raw.citations.find((c) => c.id === id))
    .filter((c): c is Citation => Boolean(c))
    .sort((a, b) => {
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      if (dateA === Infinity && dateB === Infinity) return 0;
      if (dateA === Infinity) return 1;
      if (dateB === Infinity) return -1;
      return dateB - dateA;
    });

  let citationNumber = 1;
  for (const citation of usedCitations) {
    citationMap.set(citation.id, { citation, number: citationNumber });
    citationNumber++;
  }

  // Patient summary
  rootChildren.push(
    sb.add('Text', {
      content: raw.patientSummary,
      variant: 'body',
    }),
  );

  // Expandable sections for each insight
  for (const [insightIndex, insight] of raw.previsitInsights.entries()) {
    // Build titleLinks as a sub-Spec
    let titleLinksSpec: Spec | undefined;
    const titleLinkSb = new SpecBuilder();
    const titleLinkKeys: string[] = [];

    for (const citationId of insight.citationIds) {
      if (!citationId) continue;
      const citationInfo = citationMap.get(citationId);
      if (!citationInfo) continue;
      const { citation, number } = citationInfo;
      const snippet = insight.citedTextSnippets?.[citationId];

      // Build tooltip content (children of the popover link)
      const tooltipChildren: string[] = [];

      // Source link inside tooltip with dialog action
      const dialogContentKey = titleLinkSb.add('DynamicArtifact', {
        blocks: convertChartToBlocks(citation),
      });
      tooltipChildren.push(
        titleLinkSb.add(
          'Link',
          {
            text: citation.type,
            style: 'citation-source',
            citationNumber: number,
            action: {
              type: 'dialog',
              title: citation.title ?? citation.type,
              subtitle: citation.date ?? undefined,
            },
          },
          [dialogContentKey],
        ),
      );

      // Date as separate caption
      if (citation.date) {
        tooltipChildren.push(
          titleLinkSb.add('Text', {
            content: citation.date,
            variant: 'caption',
          }),
        );
      }

      // Quoted text snippets
      if (snippet) {
        const snippets = snippet.split('\n').filter(Boolean);
        for (const s of snippets) {
          tooltipChildren.push(
            titleLinkSb.add('Text', {
              content: `"${s}"`,
              variant: 'body',
            }),
          );
        }
      }

      // The citation badge link itself (popover trigger)
      titleLinkKeys.push(
        titleLinkSb.add(
          'Link',
          {
            text: String(number),
            style: 'citation',
            citationNumber: number,
            action: { type: 'popover', trigger: 'hover' },
            analytics: buildCitationLinkAnalytics(citation, number, 'inline'),
            asSpan: true,
          },
          tooltipChildren,
        ),
      );
    }

    if (titleLinkKeys.length > 0) {
      titleLinksSpec = titleLinkSb.build(titleLinkKeys);
    }

    // Build expandable body children
    const bodyChildren: string[] = [];
    bodyChildren.push(
      sb.add('Text', {
        content: insight.description,
        variant: 'body',
        markdown: true,
      }),
    );

    // Expandable element
    rootChildren.push(
      sb.add(
        'Expandable',
        {
          title: insight.title,
          defaultOpen: expandedIndices.has(insightIndex),
          analytics: buildExpandableAnalytics(
            insight.title,
            insight.description,
          ),
          feedbackMetadata: {
            title: insight.title,
            description: insight.description,
          },
          titleLinksSpec,
        },
        bodyChildren,
      ),
    );
  }

  // Sources section
  if (citationMap.size > 0) {
    rootChildren.push(
      sb.add('Text', {
        content: 'SOURCES',
        variant: 'label',
      }),
    );

    for (const { citation, number } of citationMap.values()) {
      const dialogContentKey = sb.add('DynamicArtifact', {
        blocks: convertChartToBlocks(citation),
      });

      rootChildren.push(
        sb.add(
          'Link',
          {
            text: citation.type,
            style: 'source',
            citationNumber: number,
            action: {
              type: 'dialog',
              title: citation.title ?? citation.type,
              subtitle: citation.date ?? undefined,
            },
            analytics: buildCitationLinkAnalytics(
              citation,
              number,
              'sources_section',
            ),
          },
          [dialogContentKey],
        ),
      );
    }
  }

  return sb.build(rootChildren);
};
